import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef, EventEmitter,
  inject, Input,
  Output,
  Type,
  ViewChild
} from '@angular/core';
import { GridColumnType } from '../../../../../../models/ts/grid-column-type.model';
import { CellActionType } from '../../../../../features/bizzmine/widgets/collection-list-widget/classes/cell-action-type';
import { CellActionData } from '../../../../../features/bizzmine/widgets/collection-list-widget/interfaces/cell-action-data';
import { GridColumnBase } from '../../../../classes/list/grid-column-base';
import { CollectionListDataInstanceProperties } from '../../../../interfaces/collection-list-data-instance';
import { GridCellBaseComponent } from '../grid-cell-base/grid-cell-base.component';
import { GRID_CELL_COLUMN_COMPONENT_TYPES } from 'src/app/shared/components/grid/constants/grid-cell-column-component-types';
import { ViewContainerRefDirective } from '../../../../directives/view-container-ref.directive';
import { createViewContainerComponent } from '../../../../functions/helpers/view-container-helpers';
import { GridContextPermissionsMetadata } from '../../../../../features/bizzmine/widgets/collection-list-widget/interfaces/grid-context-permissions-metadata';
import { TaskStateType } from '../../../../../../models/ts/task-state-type.model';
import { GRID_CELL_TABLE_FIELD_COMPONENT_TYPES } from 'src/app/shared/components/grid/constants/grid-cell-component-types';
import { TableFieldDataType } from 'src/models/ts/table-field-data-type.model';
import { ChangeDetectionService } from 'src/app/core/services/change-detection/change-detection.service';
import { GridCellTextComponent } from '../grid-cell-text/grid-cell-text.component';
import { PwaService } from 'src/app/core/services/pwa/pwa.service';
import { Actions, ofType } from '@ngrx/effects';
import { formsActions } from 'src/app/store/features/forms/forms-actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IndexedDBService } from 'src/app/core/services/indexed-db/indexed-db.service';
import { KeepOfflineService } from 'src/app/core/services/keep-offline/keep-offline.service';
import { Store } from '@ngrx/store';
import { userSettingsFeature } from 'src/app/store/features/user-settings/user-settings-feature';

/**
 * Container cell class containing the specific cell class
 * depending on the data being displayed.
 */
@Component({
  selector: 'bizz-grid-cell',
  templateUrl: './grid-cell.component.html',
  styleUrls: ['./grid-cell.component.scss'],
  //TODO: investigate this: https://blog.angular-university.io/onpush-change-detection-how-it-works/
  //Might have issues when inline editing
  //changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ViewContainerRefDirective
  ]
})
export class GridCellComponent implements AfterViewInit {

  @Input({ required: true }) public gridColumn: GridColumnBase;

  private cdr = inject(ChangeDetectorRef);
  public constructor() {
    ChangeDetectionService.detach(this.cdr);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input({ required: true })
  public set data(items: CollectionListDataInstanceProperties & { [key: string]: any; } | any) {
    this._data = items;
    if (this.currentComponentType) {
      //For the audit log new value we need to recreate the component when the data changes
      //as the componenttype changes based on the row data and not the column like regular grids.
      if (this.gridColumn.GridColumnType == GridColumnType.AuditLogNewValue)
        this.loadComponent();
      this.setInstanceData(this.currentComponentType);
    }
  }
  public pwaService = inject(PwaService);
  public actions$ = inject(Actions);
  public destroyRef = inject(DestroyRef);
  public keepoOfflineService = inject(KeepOfflineService);
  protected store$ = inject(Store);
  protected enableListMultilineSignal = this.store$.selectSignal(userSettingsFeature.selectEnableListMultiline);
  

  public get data(): CollectionListDataInstanceProperties & { [key: string]: any; } | any {
    return this._data;
  }

  private _data: CollectionListDataInstanceProperties & { [key: string]: any; } | any;
  @Input() public metaData: GridContextPermissionsMetadata;
  @Output() public onCellAction = new EventEmitter<{ action: CellActionType, data: CellActionData }>();

  @ViewChild(ViewContainerRefDirective, { static: true })
  public componentHost: ViewContainerRefDirective;


  public ngAfterViewInit(): void {
    if (this.componentHost && this.gridColumn && this.data) {
      this.loadComponent();
    } else console.error('Could not load cell component');

   
  }

  public currentComponentType: GridCellBaseComponent<unknown>;

  public loadComponent(): void {
    const componentType = this.resolveGridCellComponent(this.gridColumn.GridColumnType);
    if (!componentType)
      throw new Error(`No component found for column type ${this.gridColumn.GridColumnType}`);

    const componentRef = createViewContainerComponent<GridCellBaseComponent<unknown>>
    (this.componentHost.viewContainerRef, componentType);
    this.setInstanceData(componentRef.instance);
    this.currentComponentType = componentRef.instance;
  }
  public component: GridCellBaseComponent<unknown>;

  protected setInstanceData(component: GridCellBaseComponent<unknown>): void {

    component.enableListMultiline = this.enableListMultilineSignal();
    component.value = this.getFieldValue(this.gridColumn.GridColumnType);
    component.column = this.gridColumn;
    component.data = this.data;
    component.metaData = this.metaData;
    component.onCellAction = this.onCellAction;
    this.component = component;

  }

  protected resolveGridCellComponent(columnType: GridColumnType): Type<GridCellBaseComponent<unknown>> {
    if (columnType == GridColumnType.AuditLogNewValue) {
      const componentType: TableFieldDataType = this.data?.ComponentType;
      if (componentType) {
        let type = GRID_CELL_TABLE_FIELD_COMPONENT_TYPES[componentType];
        if (type) {
          if (componentType == TableFieldDataType.Memo) {
            this.gridColumn.ColMaxChar = 75;
            this.gridColumn.ShowReadMoreIcon = true;
          }else if(componentType == TableFieldDataType.DatePicker || componentType == TableFieldDataType.DateTimePicker){
          // Use text component for date pickers to display value 
          type = GridCellTextComponent;
          }
          return type;
        } else {
          throw new Error(`Track changes: No table field component found for column type ${componentType}`);
        }
      }
    }
    const type = GRID_CELL_COLUMN_COMPONENT_TYPES[columnType];
    if (type) return type;
    else {
      throw new Error(`No component found for column type ${columnType}`);
    }
  }

  public getFieldValue(columnType: GridColumnType): unknown {
    switch (columnType) {
      case(GridColumnType.Status):
        return this.data.InstanceStatusType;
      case(GridColumnType.TaskStatus):
        return this.data[this.gridColumn.field] as TaskStateType;
      default:
        return this.data[this.gridColumn.field];
    }
  }
}

export interface DynamicCellComponent<T> extends Component {
  column: GridColumnBase;
  data: CollectionListDataInstanceProperties & { [key: string]: T; };
  value: T | undefined;
}
